/**
 * Auth + session store (zustand).
 *
 * Holds the Atlas access token, its decoded claims (tenant_id, sid, roles,
 * permissions), the active tenant code, and the pending idempotency key used
 * by the API client for replay-safe mutations.
 *
 * MEMORY ONLY (S2-05, docs/design/tokens.md §2.8). This store used to be
 * persisted to localStorage under `atlas-auth`, which kept a bearer token
 * where any script on the page could read it and carry it off the machine.
 * Nothing here is written to any storage now. What survives a reload is the
 * HttpOnly refresh cookie, which page script cannot read: on load,
 * restoreSession() (lib/auth/refresh.ts) exchanges it for a new access token,
 * and removeLegacyToken() deletes the key earlier builds left behind.
 */
import { create } from "zustand";

/**
 * Why the API refused the workspace (403 from the session gate). Held only in
 * memory: a reload asks the server again, which is the right "retry".
 */
export interface WorkspaceBlock {
  code: "TENANT_SUSPENDED" | "TENANT_NOT_ACTIVE";
  detail: string;
}

export interface AtlasClaims {
  sub: string;
  tenant_id: string;
  cell_id: string;
  zone_id: string;
  tenant_user_id: string;
  roles: string[];
  permissions: string[];
  exp: number;
  iat: number;
  jti: string;
  /**
   * The session ("UserSession".id) this token belongs to. Every token minted
   * for one sign-in carries the same sid, so a refresh keeps it and a new
   * sign-in changes it. Absent on a token issued before the person has a
   * workspace: that token has no session and no refresh (design §2.10).
   */
  sid?: string;
  actor_type?: "User" | "ApiClient";
}

/**
 * - restoring: the page has just loaded and is asking the server, through
 *   the refresh cookie, whether a session exists. Protected screens wait.
 * - signedIn: a token is in memory.
 * - anonymous: no session.
 */
export type SessionStatus = "restoring" | "signedIn" | "anonymous";

interface AuthState {
  accessToken: string | null;
  claims: AtlasClaims | null;
  activeTenantCode: string | null;
  sessionStatus: SessionStatus;
  /**
   * Why the last restore could not reach the server (network, 5xx), while
   * the status stays "restoring": protected screens show it with Try again
   * instead of a skeleton that never ends. Null otherwise.
   */
  restoreError: unknown;
  mfaRequired: boolean;
  /**
   * Carries "password accepted" between the two sign-in steps. Short-lived
   * and half of an authentication, so it does not survive a page the user
   * walked away from.
   */
  mfaToken: string | null;
  pendingIdempotencyKey: string | null;
  /** Shown once on the sign-in page after the server ended the session. */
  sessionNotice: string | null;
  /** Set when the API says the active workspace is not Active. */
  workspaceBlock: WorkspaceBlock | null;
  /**
   * A new session: a sign-in, a restore, or another session adopted. The
   * workspace code, when given, is set in the same update, so nothing
   * observes the new session under the old workspace.
   */
  setSession: (
    token: string,
    claims: AtlasClaims,
    activeTenantCode?: string,
  ) => void;
  /**
   * The same session with a new access token (a refresh). Unlike setSession
   * it keeps the workspace block and the notice: a renewal is not a new
   * session, and it does not empty the query cache (keyed on the sid).
   */
  renewSession: (token: string, claims: AtlasClaims) => void;
  /** The page is asking the server for its session; no-op when one is held. */
  beginRestore: () => void;
  /** The restore could not reach the server; the status stays "restoring". */
  restoreFailed: (error: unknown) => void;
  setMfaChallenge: (token: string | null) => void;
  setActiveTenantCode: (code: string) => void;
  setMfaRequired: (value: boolean) => void;
  setPendingIdempotencyKey: (key: string) => void;
  clearPendingIdempotencyKey: () => void;
  logout: () => void;
  /** Clear the session because the server ended it, leaving a notice. */
  endSession: (notice: string) => void;
  setWorkspaceBlock: (block: WorkspaceBlock | null) => void;
  hasPermission: (code: string) => boolean;
  hasRole: (code: string) => boolean;
}

export const useAuthStore = create<AuthState>()((set, get) => ({
  accessToken: null,
  claims: null,
  activeTenantCode: null,
  // Nothing is known before the page asks; main.tsx calls restoreSession()
  // (which moves this to "restoring") before the first render.
  sessionStatus: "anonymous",
  restoreError: null,
  mfaRequired: false,
  mfaToken: null,
  pendingIdempotencyKey: null,
  sessionNotice: null,
  workspaceBlock: null,
  // Establishing a session always clears the half-finished one, and any
  // notice or block left by the session before it.
  setSession: (token, claims, activeTenantCode) =>
    set({
      accessToken: token,
      claims,
      ...(activeTenantCode !== undefined ? { activeTenantCode } : {}),
      sessionStatus: "signedIn",
      restoreError: null,
      mfaToken: null,
      mfaRequired: false,
      sessionNotice: null,
      workspaceBlock: null,
    }),
  renewSession: (token, claims) =>
    set({
      accessToken: token,
      claims,
      sessionStatus: "signedIn",
      restoreError: null,
    }),
  beginRestore: () => {
    if (!get().accessToken)
      set({ sessionStatus: "restoring", restoreError: null });
  },
  restoreFailed: (error) => {
    if (get().sessionStatus === "restoring") set({ restoreError: error });
  },
  setMfaChallenge: (token) => set({ mfaToken: token }),
  // A block belongs to the workspace that returned it, not to the next.
  setActiveTenantCode: (code) =>
    set({ activeTenantCode: code, workspaceBlock: null }),
  setMfaRequired: (value) => set({ mfaRequired: value }),
  setPendingIdempotencyKey: (key) => set({ pendingIdempotencyKey: key }),
  clearPendingIdempotencyKey: () => set({ pendingIdempotencyKey: null }),
  logout: () =>
    set({
      accessToken: null,
      claims: null,
      activeTenantCode: null,
      sessionStatus: "anonymous",
      restoreError: null,
      mfaRequired: false,
      mfaToken: null,
      sessionNotice: null,
      workspaceBlock: null,
    }),
  endSession: (notice) => {
    get().logout();
    set({ sessionNotice: notice });
  },
  setWorkspaceBlock: (block) => set({ workspaceBlock: block }),
  hasPermission: (code) => {
    const claims = get().claims;
    if (!claims) return false;
    return (
      claims.permissions.includes("*") ||
      claims.permissions.includes(code) ||
      claims.roles.includes("platform_admin")
    );
  },
  hasRole: (code) => get().claims?.roles.includes(code) ?? false,
}));

/**
 * What cached data and the person's locale are bound to: the session, not
 * the token string. A refresh replaces the token every 15 minutes and keeps
 * the sid, so keying on the token emptied every screen's cache and reloaded
 * /users/me four times an hour. A different person or a new sign-in has a
 * different sid, so D13 (the second person's screens opening on the first
 * person's data) still holds. A token with no sid (issued before a
 * workspace exists) is its own identity.
 */
export function sessionIdentity(state: {
  accessToken: string | null;
  claims: AtlasClaims | null;
}): string | null {
  if (!state.accessToken) return null;
  return state.claims?.sid ? `sid:${state.claims.sid}` : state.accessToken;
}
